import Image from "next/image";
import Link from "next/link";
import type { Creator } from "@/lib/types";

type Props = { creator: Creator };

const habitatLabels: Record<string, string> = {
  reef: "Reef",
  freshwater: "Freshwater",
  "deep-sea": "Deep sea",
  coastal: "Coastal",
};

export function CreatorCard({ creator }: Props) {
  return (
    <article className="group overflow-hidden rounded-2xl border border-white/10 bg-white/5 shadow-lg transition hover:border-teal-400/40 hover:bg-white/10">
      <Link href={`/creators/${creator.slug}`} className="block">
        <div className="relative aspect-[21/9] w-full overflow-hidden">
          <Image
            src={creator.coverUrl}
            alt=""
            fill
            className="object-cover transition duration-500 group-hover:scale-105"
            sizes="(max-width: 768px) 100vw, 33vw"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#042f2e] via-transparent to-transparent" />
        </div>
        <div className="relative -mt-10 flex gap-4 px-4 pb-4 sm:px-5 sm:pb-5">
          <div className="relative h-20 w-20 shrink-0 overflow-hidden rounded-2xl border-4 border-[#042f2e] bg-[#042f2e] shadow-md">
            <Image
              src={creator.avatarUrl}
              alt=""
              fill
              className="object-cover"
              sizes="80px"
            />
          </div>
          <div className="min-w-0 flex-1 pt-8">
            <p className="text-xs font-medium uppercase tracking-wider text-teal-300/90">
              {creator.role}
            </p>
            <h2 className="truncate text-lg font-semibold text-white">
              {creator.displayName}
            </h2>
            <p className="mt-1 line-clamp-2 text-sm text-teal-100/75">
              {creator.bio}
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              {creator.habitats.map((h) => (
                <span
                  key={h}
                  className="rounded-full bg-black/30 px-2.5 py-0.5 text-xs text-teal-100/90"
                >
                  {habitatLabels[h]}
                </span>
              ))}
            </div>
          </div>
        </div>
        <div className="flex items-center justify-between border-t border-white/10 px-4 py-3 sm:px-5">
          <div>
            <p className="text-xs text-teal-200/70">School</p>
            <p className="font-semibold text-amber-200">
              ${creator.schoolPriceUsd}
              <span className="text-sm font-normal text-teal-100/70">/mo</span>
            </p>
          </div>
          <div className="text-right text-sm text-teal-100/80">
            <span className="tabular-nums">
              {creator.schoolCount.toLocaleString()}
            </span>{" "}
            members
          </div>
        </div>
      </Link>
    </article>
  );
}
